import Checkbox, { CustomCheckboxProps } from '../../inputs/Checkbox';
import { FormControl, FormControlLabel, FormControlLabelProps, FormGroup, IconButton, Tooltip } from '@mui/material';

import { TID_ATTR } from '@/common/infrastructure/config/tid';
import { FormikFieldWithTooltipProps } from './types/FormikFieldProps';
import FormHelperText from '@mui/material/FormHelperText';
import { FormikFieldSkeleton } from './FormikFieldSkeleton';
import { useFormikField } from './hooks/useFormikField';

const LabelComponent = <T extends Record<string, unknown>>({
  tooltipText,
  label,
  tooltipPlacement,
  iconEndAdornment
}: Pick<FormikFieldWithTooltipProps<T>, 'tooltipText' | 'label' | 'tooltipPlacement' | 'iconEndAdornment'>) =>
  tooltipText ? (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      {label}
      <Tooltip title={tooltipText} placement={tooltipPlacement} arrow>
        <IconButton>{iconEndAdornment}</IconButton>
      </Tooltip>
    </span>
  ) : (
    label
  );

export const FormikCheckbox = <T extends Record<string, unknown>>({
  id,
  label,
  tooltipText,
  tooltipPlacement = 'top',
  iconEndAdornment,
  formControlLabelProps,
  testId,
  ...rest
}: FormikFieldWithTooltipProps<T> &
  CustomCheckboxProps & {
    formControlLabelProps?: Omit<FormControlLabelProps, 'control' | 'label'>;
    testId?: string;
  }) => {
  const { [TID_ATTR]: restTestId, ...restWithoutTestId } = rest as Record<string, unknown> & { [TID_ATTR]?: string };
  const baseTestId = testId ?? restTestId;
  const { status, setFieldValue, handleBlur, errorText, hasError, fieldValue } = useFormikField<T>({ id });

  // Provide a default value (false) if fieldValue is undefined
  const checked = fieldValue !== undefined ? Boolean(fieldValue) : false;

  if (status?.loading) {
    return <FormikFieldSkeleton />;
  }

  const existingInputProps = (restWithoutTestId.inputProps ?? {}) as Record<string, unknown>;
  const inputProps = baseTestId ? { ...existingInputProps, [TID_ATTR]: `${baseTestId}:input` } : existingInputProps;
  const labelTestId = baseTestId ? { [TID_ATTR]: `${baseTestId}:label` } : {};
  const helperTestId = baseTestId ? { [TID_ATTR]: `${baseTestId}:helper-text` } : {};
  const containerTestId = baseTestId ? { [TID_ATTR]: baseTestId } : {};
  const { inputProps: _omit, ...restCheckboxProps } = restWithoutTestId as Record<string, unknown> & {
    inputProps?: Record<string, unknown>;
  };

  return (
    <FormControl variant="outlined" fullWidth error={hasError} {...containerTestId}>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              id={String(id)}
              name={String(id)}
              checked={checked}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFieldValue(String(id), event.target.checked)}
              onBlur={handleBlur}
              inputProps={Object.keys(inputProps).length ? inputProps : undefined}
              {...restCheckboxProps}
            />
          }
          label={
            <span {...labelTestId}>
              <LabelComponent
                tooltipText={tooltipText}
                label={label}
                tooltipPlacement={tooltipPlacement}
                iconEndAdornment={iconEndAdornment}
              />
            </span>
          }
          {...formControlLabelProps}
        />
      </FormGroup>
      <FormHelperText {...helperTestId}>{errorText}</FormHelperText>
    </FormControl>
  );
};
